import React, { useState } from 'react';
import { XIcon } from './icons/InterfaceIcons';

interface MobileQuestionPaletteProps {
  questions: any[];
  statuses: Map<number, string>;
  currentIndex: number;
  navigateTo: (index: number) => void;
  onClose: () => void;
}

export const MobileQuestionPalette: React.FC<MobileQuestionPaletteProps> = ({
  questions,
  statuses,
  currentIndex,
  navigateTo,
  onClose
}) => {
  const [filter, setFilter] = useState('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'answered': return 'bg-green-500 text-white';
      case 'notAnswered': return 'bg-red-500 text-white';
      case 'marked': return 'bg-purple-500 text-white';
      case 'answeredAndMarked': return 'bg-blue-500 text-white';
      default: return 'bg-brand-surface border border-brand-border text-brand-text-primary';
    }
  };

  const getFilteredQuestions = () => {
    return questions.map((_, index) => ({ index, status: statuses.get(index) }))
      .filter(({ status }) => {
        if (filter === 'all') return true;
        if (filter === 'answered') return status === 'answered' || status === 'answeredAndMarked';
        if (filter === 'unanswered') return status === 'notAnswered' || status === 'marked';
        if (filter === 'marked') return status === 'marked' || status === 'answeredAndMarked';
        return true;
      });
  };

  const filteredQuestions = getFilteredQuestions();
  const stats = {
    answered: questions.filter((_, i) => ['answered', 'answeredAndMarked'].includes(statuses.get(i) || '')).length,
    unanswered: questions.filter((_, i) => ['notAnswered', 'marked'].includes(statuses.get(i) || '')).length,
    marked: questions.filter((_, i) => ['marked', 'answeredAndMarked'].includes(statuses.get(i) || '')).length
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-brand-surface w-full max-h-[80vh] rounded-t-xl">
        <div className="flex items-center justify-between p-4 border-b border-brand-border">
          <h3 className="font-bold text-lg">Question Palette</h3>
          <button onClick={onClose} className="p-2 hover:bg-brand-bg rounded-lg">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: 'all', label: 'All', count: questions.length },
              { key: 'answered', label: 'Answered', count: stats.answered },
              { key: 'unanswered', label: 'Unanswered', count: stats.unanswered },
              { key: 'marked', label: 'Marked', count: stats.marked }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  filter === key 
                    ? 'bg-brand-primary text-white' 
                    : 'bg-brand-bg text-brand-text-secondary'
                }`}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto">
            {filteredQuestions.map(({ index }) => (
              <button
                key={index}
                onClick={() => {
                  navigateTo(index);
                  onClose();
                }}
                className={`h-12 flex items-center justify-center rounded font-semibold transition-all ${
                  getStatusColor(statuses.get(index) || '')
                } ${
                  currentIndex === index ? 'ring-2 ring-brand-primary ring-offset-2' : ''
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center"><span className="w-3 h-3 rounded bg-green-500 mr-2"></span>Answered</div>
            <div className="flex items-center"><span className="w-3 h-3 rounded bg-red-500 mr-2"></span>Not Answered</div>
            <div className="flex items-center"><span className="w-3 h-3 rounded bg-purple-500 mr-2"></span>Marked</div>
            <div className="flex items-center"><span className="w-3 h-3 rounded bg-blue-500 mr-2"></span>Answered & Marked</div>
          </div>
        </div>
      </div>
    </div>
  );
};